import { push } from 'connected-react-router';
import { all, call, put, race, select, spawn, take } from 'redux-saga/effects';
import { accountBalanceRequest } from '../../modules/account/actions';
import { serviceUnavailable } from '../../modules/app/actions';
import { ConnectionError, NotFoundError, ServiceUnavailableError } from '../../modules/app/errors';
import { close, open } from '../../modules/modal/actions';
import { OMNI_TRANSFER, SETTLED_BETS } from '../../routes/paths';
import * as form from '../FormMessage/actions';
import i18n from '../i18n/i18n';
import { getLocale } from '../i18n/selectors';
import {
  ATTEMPT,
  CONFIRM,
  failure,
  pinFailure,
  REJECT,
  REQUEST,
  success,
  VIEW_ENTER
} from './actions';
import {
  REASON_PIN_BLOCKED,
  REASON_PIN_INVALID,
  REASON_TOO_MANY_METHODS,
  TransferBadRequestError,
  TransferForbiddenError
} from './errors';
import { putTransfer } from './rest';
import { canTransferSlip, countBetsToTransfer, getTransferSlipId } from './selectors';

/* eslint-disable no-constant-condition */
export function* watchAttempt() {
  while (yield take(ATTEMPT)) {
    const betsInSlip = yield select(countBetsToTransfer);
    const canTransfer = yield select(canTransferSlip);
    yield put(form.clearMessage());
    let confirmResult = { confirm: false };

    if (betsInSlip > 1 && !canTransfer) {
      yield put(open({ componentName: 'SlipNotSettled' }));
    }
    if (betsInSlip > 1 && canTransfer) {
      yield put(open({ componentName: 'SlipTransferConfirm' }));
      confirmResult = yield race({
        confirm: take(CONFIRM),
        reject: take(REJECT)
      });
      yield put(close());
    }
    if (canTransfer && (betsInSlip === 1 || confirmResult.confirm)) {
      yield put(push(OMNI_TRANSFER));
    }
  }
}

export function* watchViewEnter() {
  while (yield take(VIEW_ENTER)) {
    const slipId = yield select(getTransferSlipId);
    if (!slipId) {
      yield put(push(SETTLED_BETS));
    }
  }
}

export function* watchRequest() {
  while (true) {
    const { pin } = yield take(REQUEST);
    const slipId = yield select(getTransferSlipId);
    const locale = yield select(getLocale);
    yield put(form.clearMessage());
    try {
      yield put(open({ componentName: 'RequestInProgress' }));
      yield call(putTransfer, slipId, pin);
      yield put(close());
      yield put(success(slipId));
      yield put(accountBalanceRequest());
      yield put(push(SETTLED_BETS));
      yield put(form.setMessage(form.SUCCESS, i18n('transfer', 'requestSuccess', locale)));
    } catch (e) {
      yield put(close());
      const isBadRequest = e instanceof TransferBadRequestError;
      const isPinInvalid = isBadRequest && e.details.reason === REASON_PIN_INVALID;
      const isForbidden = e instanceof TransferForbiddenError;
      const isPinBlocked = isForbidden && e.details.reason === REASON_PIN_BLOCKED;
      const isTooManyMethods = isForbidden && e.details.reason === REASON_TOO_MANY_METHODS;
      const isNotFound = e instanceof NotFoundError;
      const isConnectionError = e instanceof ConnectionError;
      const isCommonError = isBadRequest || isForbidden || isNotFound || isConnectionError;
      const isUnavailable = e instanceof ServiceUnavailableError;
      if (isPinInvalid) {
        yield put(pinFailure(e));
        yield put(form.setMessage(form.ERROR,
          i18n('common', 'incorrectPIN', locale, { attempts: e.details.remainingAttempts })
        ));
      } else if (isPinBlocked) {
        yield put(failure(e));
        yield put(push(SETTLED_BETS));
        yield put(form.setMessage(form.ERROR, i18n('common', 'blockedPIN', locale)));
      } else if (isTooManyMethods) {
        yield put(failure(e));
        yield put(push(SETTLED_BETS));
        yield put(form.setMessage(form.ERROR, i18n('transfer', 'errorPaymentMethods', locale)));
      } else if (isCommonError) {
        yield put(failure(e));
        yield put(push(SETTLED_BETS));
        yield put(form.setMessage(form.ERROR, 'TransferFailed', null, false));
      } else if (isUnavailable) {
        yield put(failure(e));
        yield put(serviceUnavailable());
      } else {
        yield put(failure(e));
        yield put(push(SETTLED_BETS));
        throw e;
      }
    }
  }
}

export default function* root() {
  yield all([
    spawn(watchAttempt),
    spawn(watchViewEnter),
    spawn(watchRequest)
  ]);
}
/* eslint-enable no-constant-condition */
